/**
 * The materials the title and story scenes draw with, all from `src/render/materials/` (they write
 * the second render target the post pass needs), made once per scene and freed with it. Nothing here
 * is depth-biased: with no stage there are no blocks for a figure to be hidden behind.
 */

import type { Material, Texture } from 'three';
import { createGlassBubbleMaterial } from '../materials/glassBubble';
import { createEmissiveMaterial, createToonMaterial, OUTLINE } from '../materials/toon';
import type { Palette } from '../palette';

export interface TitleMaterials {
  /** The logo's letters: a figure's ink outline. */
  logo: Material;
  /** Bub and Bob. */
  dragon: Material;
  /** The title's floor blocks. */
  block: Material;
  /** Stars. */
  glow: Material;
  /** The dark wall behind everything: no outline of its own. */
  wall: Material;
  /** Glass as Bub blows it (green) and as Bob does (blue), as in the game (lookdev). */
  glassBub: Material;
  glassBob: Material;
  /** Everything above, to free. */
  all: readonly Material[];
}

export function createTitleMaterials(palette: Palette, gradientMap: Texture): TitleMaterials {
  const glass = (ramp: string): Material => createGlassBubbleMaterial({
    tint: palette.color(`${ramp}.1`), rim: palette.color(`${ramp}.2`), depthBias: 0,
  });
  const m = {
    logo: createToonMaterial({ gradientMap, outline: OUTLINE.character }),
    dragon: createToonMaterial({ gradientMap, outline: OUTLINE.player }),
    block: createToonMaterial({ gradientMap, outline: OUTLINE.block }),
    glow: createEmissiveMaterial(gradientMap, 1.6),
    wall: createToonMaterial({ gradientMap, outline: OUTLINE.none }),
    glassBub: glass('bubble'),
    glassBob: glass('bob'),
  };
  return { ...m, all: Object.values(m) };
}
